import cx from 'clsx';
import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Navigation, Pagination, Swiper as SwiperType } from 'swiper';
import 'swiper/css/pagination';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper.min.css';

import { ReactComponent as EditIcon } from '@assets/svg/ic-edit-pencil.svg';
import { ReactComponent as SwiperArrowIcon } from '@assets/svg/ic-swiper-arrow.svg';

import SkeletonBlock from '@components/common/Skeleton/SkeletonBlock';
import { IMAGE_TYPES } from '@components/modals/EditSensorGalleryModal/constants/imageTypes';
import { useEquipmentImagesListQuery } from '@components/modals/EditSensorGalleryModal/services/useEquipmentImagesListQuery';

import { MODAL_NAMES } from '@constants/modalRenderers';

import useTranslation from '@hooks/useTranslation';

import type { IInstallationPoint } from '@model/installationPoint';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import EmptySensorGalleryBlock from '../EmptySensorGalleryBlock/EmptySensorGalleryBlock';
import style from './style.module.scss';

interface IProps {
  installationPoints?: IInstallationPoint[];
  equipmentName?: string;
  isInstallationPointsFetching?: boolean;
}

const SensorGallery = ({ installationPoints, equipmentName, isInstallationPointsFetching }: IProps) => {
  const { t } = useTranslation();
  const { equipmentId } = useParams();

  const swiperRef = useRef<SwiperType | null>();
  const { setActiveModal } = useModalActions();
  const { data: equipmentImages, isFetching } = useEquipmentImagesListQuery({ equipment_id: equipmentId });
  const { data: profileData } = useUserProfileQuery();
  const hasPermissions = profileData?.userAccess?.manageEquipmentDeactivation;

  const [isNavButtons, setNavButtons] = useState<boolean>(false);

  if (isFetching || isInstallationPointsFetching) {
    return <SkeletonBlock className={style.skeleton} />;
  }

  if (!equipmentImages?.length) {
    return (
      <EmptySensorGalleryBlock
        images={equipmentImages}
        equipmentName={equipmentName}
        installationPoints={installationPoints}
      />
    );
  }
  return (
    <div className={style.sensorGallery}>
      <Swiper
        className={cx(style.gallerySwiper, {
          [style.twoBulletPagination]:
            equipmentImages.length - (swiperRef.current?.params?.slidesPerView as number) === 1,
          [style.threeBulletPagination]:
            equipmentImages.length - (swiperRef.current?.params?.slidesPerView as number) === 2,
          [style.fourBulletPagination]:
            equipmentImages.length - (swiperRef.current?.params?.slidesPerView as number) === 3,
          [style.fiveBulletPagination]:
            equipmentImages.length - (swiperRef.current?.params?.slidesPerView as number) === 4,
        })}
        modules={[Pagination, Navigation]}
        spaceBetween={20}
        onSwiper={(swiper) => {
          setNavButtons(swiper.slides.length > (swiper.params?.slidesPerView as number));
        }}
        slidesPerView={3}
        breakpoints={{
          2800: {
            slidesPerView: 8,
          },
          2400: {
            slidesPerView: 7,
          },
          2000: {
            slidesPerView: 6,
          },
          1300: {
            slidesPerView: 5,
          },
          1000: {
            slidesPerView: 4,
          },
        }}
        pagination={{
          clickable: true,
          dynamicBullets: equipmentImages.length > 5 && true,
          dynamicMainBullets: 4,
        }}
        onBeforeInit={(swiper) => (swiperRef.current = swiper)}
      >
        {equipmentImages?.map((img) => {
          const isAllChecked = img.installationPoint?.length === installationPoints?.length;
          const installationPointImageName =
            img.installationPoint?.length === 1
              ? img.installationPoint[0].name
              : t(`Installation Point ({count})`, { count: img.installationPoint?.length });
          const installationPointImageLabel = isAllChecked ? equipmentName : installationPointImageName;

          return (
            <SwiperSlide className={style.slide} key={img.id}>
              <img src={img.url} alt={img.name} className={style.galleryImage} />
              <div className={style.slideLabels}>
                <span className={style.typeLabel}>{IMAGE_TYPES[img.imageType]}</span>
                {img.installationPoint?.length && img.imageType !== 'primaryImage' && (
                  <span
                    className={style.pointLabel}
                    dangerouslySetInnerHTML={{
                      __html: installationPointImageLabel,
                    }}
                  />
                )}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
      {isNavButtons && (
        <>
          <button className={cx(style.navBtn, style.prevBtn)} onClick={() => swiperRef.current?.slidePrev()}>
            <SwiperArrowIcon className={style.prevBtnIcon} />
          </button>
          <button className={cx(style.navBtn, style.nextBtn)} onClick={() => swiperRef.current?.slideNext()}>
            <SwiperArrowIcon className={style.nextBtnIcon} />
          </button>
        </>
      )}
      {hasPermissions && (
        <EditIcon
          className={style.editGalleryButton}
          onClick={() =>
            setActiveModal({
              name: MODAL_NAMES.editSensorGalleryModal,
              options: {
                images: equipmentImages,
                installationPoints: installationPoints,
                equipmentName: equipmentName,
              },
            })
          }
        />
      )}
    </div>
  );
};

export default SensorGallery;
